import clsx from "clsx";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NotificationStack,
  NotificationType,
} from "@transfr-inc/dashboard-components";
import { Button } from "@transfr-inc/dashboard-components/forms";

import { trackClassroomsDeleted } from "@lib/event-trackers";
import container from "@src/container";

import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";

import "./delete-modal.scss";

export const BulkDeleteClassroomModal = ({
  onCancel,
  show,
  className,
  deleteList,
  onRowRemoved,
}) => {
  const [notifications, setNotifications] = useState();
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const { classroomService } = container;

  const onCloseNotification = (id) => {
    setNotifications((n) => n.filter((notification) => notification.id !== id));
  };

  const showNotification = (type, message, icon) => {
    const notification = { type, message, icon, id: crypto.randomUUID() };
    setNotifications((n) => {
      const notificationsUpdated = n ?? [];
      return [...notificationsUpdated, notification];
    });
  };

  const { handleSubmit, reset } = useForm({
    defaultValues: { ...deleteList },
  });

  const getContent = (deleteList) => {
    return deleteList.length == 1 ? (
      <div className="delete-content">
        Are you sure you would like to delete <b>{deleteList[0].name}</b>? This
        action cannot be undone. A classroom can not be deleted while{" "}
        {getLearnerTerm({ lowercase: true, plural: true })} are enrolled.
      </div>
    ) : (
      <div className="delete-content">
        Are you sure you would like to delete
        <b> {deleteList.length} </b>
        classrooms? This action cannot be undone. A classroom can not be deleted
        while {getLearnerTerm({ lowercase: true, plural: true })} are enrolled.
      </div>
    );
  };

  async function submitForm(values, _) {
    try {
      // Not allowing user to delete classroom with students (except PL classrooms)
      const [classroomsWithStudents, classroomsWithoutStudents] = values.reduce(
        ([p, f], e) =>
          e.totalTrainees > 0 && !e.isProfessionalLearning
            ? [[...p, e], f]
            : [p, [...f, e]],
        [[], []]
      );

      if (classroomsWithStudents.length > 0) {
        showNotification(
          NotificationType.error,
          classroomsWithStudents.length > 1 ? (
            <div>
              <b>{classroomsWithStudents.length}</b> classrooms with{" "}
              {getLearnerTerm({ lowercase: true, plural: true })} enrolled could
              not be deleted.
            </div>
          ) : (
            <div>
              <b>{classroomsWithStudents[0].name}</b> cannot be deleted while
              there are {getLearnerTerm({ lowercase: true, plural: true })}{" "}
              enrolled.
            </div>
          )
        );
      }

      if (classroomsWithoutStudents.length > 0) {
        const classroomIds = classroomsWithoutStudents.map(
          (x) => x["classroomId"]
        );
        await classroomService.deleteClassrooms({
          classroomIds: classroomIds,
        });
        trackClassroomsDeleted(classroomsWithoutStudents, {
          batchId: crypto.randomUUID(),
        });
        onRowRemoved(classroomIds);
        showNotification(
          NotificationType.success,
          classroomsWithoutStudents.length > 1 ? (
            <div>
              <b>{classroomsWithoutStudents.length}</b> classrooms have been
              deleted.
            </div>
          ) : (
            <div>
              <b>{classroomsWithoutStudents[0].name}</b> has been deleted.
            </div>
          )
        );
      }
    } catch (error) {
      console.log(error);
      console.trace();
      showNotification(NotificationType.error, "Something went wrong");
    } finally {
      onCancel();
      setIsAlertVisible(true);
      setTimeout(() => {
        setIsAlertVisible(false);
        setNotifications();
      }, 10000);
    }
  }

  const onClick = (_, e) => {
    handleSubmit(submitForm)(e);
  };

  useEffect(() => {
    reset(deleteList);
  }, [deleteList]);

  return (
    <div className={clsx("delete-modal-container", className)}>
      <Modal modalClassName={"delete-modal"} open={show} onClose={onCancel}>
        <ModalHeader
          className="red-icon"
          icon={["fa-regular", "fa-trash-can"]}
          title={
            deleteList.length > 1 ? "Delete Classrooms" : "Delete Classroom"
          }
        ></ModalHeader>
        <ModalBody className="delete-body">{getContent(deleteList)}</ModalBody>
        <ModalFooter>
          <Button onClick={onCancel}>Cancel</Button>
          <Button destructive onClick={onClick}>
            {deleteList.length > 1
              ? `Yes, Delete Classrooms`
              : `Yes, Delete Classroom`}
          </Button>
        </ModalFooter>
      </Modal>
      <div className="success-notification">
        {isAlertVisible && notifications?.length > 0 && (
          <NotificationStack
            notifications={notifications}
            onClose={onCloseNotification}
            closable
          ></NotificationStack>
        )}
      </div>
    </div>
  );
};
